'use strict';
import randomInt from '../../utils/randomInt';
import Team from './Team';
import Board from './Board';

class Game {
  #isStarted = false;
  #currentTeam = null;
  #teamA = Team.A();
  #teamB = Team.B();
  #board = null;
  #gameOverCallback = null;
  #gameOver = (winner) => {
    this.#isStarted = false;
    this.#gameOverCallback(winner);
  };

  constructor(gameOverCallback) {
    this.#gameOverCallback = gameOverCallback;
  }

  set colorTeamA(color) {
    if (this.#isStarted) {
      return;
    }

    this.#teamA.color = color;
  }

  set colorTeamB(color) {
    if (this.#isStarted) {
      return;
    }

    this.#teamB.color = color;
  }

  get isStarted() {
    return this.#isStarted;
  }

  get currentTeam() {
    return this.#currentTeam;
  }

  addPlayer(team, player) {
    this.removePlayer(player);

    if (team === 'A') {
      this.#teamA.addPlayer(player);
    }

    if (team === 'B') {
      this.#teamB.addPlayer(player);
    }
  }

  removePlayer(player) {
    this.#teamA.removePlayer(player);
    this.#teamB.removePlayer(player);
  }

  reveal(player, cellIndex) {
    if (!this.#isStarted) {
      return;
    }

    if (
      player.team !== this.#currentTeam ||
      this.#teamA.isCodeMaster(player) ||
      this.#teamB.isCodeMaster(player)
    ) {
      return;
    }

    const type = this.#board.reveal(cellIndex);
    if (type === 'black') {
      this.endTurn();
      this.#gameOver(this.#currentTeam);
      return;
    }

    if (this.#board.missingWordsTeamA === 0) {
      this.#gameOver('A');
    }

    if (this.#board.missingWordsTeamB === 0) {
      this.#gameOver('B');
    }

    if (type !== player.team) {
      this.endTurn();
    }
  }

  defineCodeMaster(player) {
    switch (player.team) {
      case 'A':
        this.#teamA.defineCodeMaster(player);
        break;

      case 'B':
        this.#teamB.defineCodeMaster(player);
        break;
    }
  }

  isCodeMaster(player) {
    return this.#teamA.isCodeMaster(player) || this.#teamB.isCodeMaster(player);
  }

  resetCodeMaster() {
    this.#teamA.resetCodeMaster();
    this.#teamB.resetCodeMaster();
  }

  endTurn() {
    this.#currentTeam = this.#currentTeam === 'A' ? 'B' : 'A';
  }

  checkGameRequirements(wordList) {
    const errors = [];
    if (wordList.length <= Board.totalFields) {
      errors.push('Not enough words');
    }
    if (this.#teamA.player.length < 2) {
      errors.push('Not enough player in team A');
    }
    if (this.#teamB.player.length < 2) {
      errors.push('Not enough player in team B');
    }
    if (!this.#teamA.hasCodeMaster()) {
      errors.push('No codemaster for team A');
    }
    if (!this.#teamB.hasCodeMaster()) {
      errors.push('No codemaster for team B');
    }

    return errors;
  }

  start(wordList) {
    if (this.#isStarted) {
      return ['Game already started'];
    }

    const errors = this.checkGameRequirements(wordList);
    if (errors.length > 0) {
      return errors;
    }

    this.#isStarted = true;
    this.#currentTeam = randomInt(0, 1) ? 'A' : 'B';
    this.#board = new Board(this.#currentTeam, wordList);

    return [];
  }

  toJson() {
    return {
      isStarted: this.isStarted,
      currentTeam: this.#currentTeam,
      teamA: this.#teamA.toJson(),
      teamB: this.#teamB.toJson(),
      board: this.#board === null ? [] : this.#board.toJson(),
    };
  }
}

export default Game;
